import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { type DisplayRowItem, QuestionOp } from '~/utils'

import { hiddenRowIdsAtom } from '../atoms'
import {
  type DisplayRowId,
  indicatorsForFiveSegs,
  indicatorsForNineSegs,
} from '../utils'

import { useAnswersArrayData } from './useAnswersArrayData'
import { useCustomerAttributesArrayData } from './useCustomerAttributesArrayData'
import { useIndicatorLabelMap } from './useIndicatorLabelMap'

export const useDisplayRowItem = (): DisplayRowItem<DisplayRowId>[] => {
  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)
  const answersArrayData = useAnswersArrayData()
  const customerAttributesArrayData = useCustomerAttributesArrayData()
  const indicatorLabelMap = useIndicatorLabelMap()
  const { t } = useTranslation()

  return React.useMemo(
    () => [
      {
        label: t('brand_name_5'),
        subItems: [...indicatorsForFiveSegs, ...indicatorsForNineSegs].map(
          (indicator) => ({
            id: indicator,
            isVisible: !hiddenRowIds.includes(indicator),
            label: indicatorLabelMap[indicator],
          }),
        ),
      },
      ...answersArrayData.map<DisplayRowItem<DisplayRowId>>((answersData) => ({
        label: QuestionOp.toText(answersData[0].question),
        subItems: answersData.map((answerData) => {
          const id = `${answerData.question.question_id}-${answerData.answerOption.option}`

          return {
            id,
            isVisible: !hiddenRowIds.includes(id),
            label: answerData.answerOption.text,
          }
        }),
      })),
      ...customerAttributesArrayData.map<DisplayRowItem<DisplayRowId>>(
        (customerAttributesData) => ({
          label: customerAttributesData[0].customerAttributeGroup.name,
          subItems: customerAttributesData.map((customerAttributeData) => {
            const id = customerAttributeData.customerAttribute.id

            return {
              id,
              isVisible: !hiddenRowIds.includes(id),
              label: customerAttributeData.customerAttribute.name,
            }
          }),
        }),
      ),
    ],
    [
      answersArrayData,
      customerAttributesArrayData,
      hiddenRowIds,
      indicatorLabelMap,
      t,
    ],
  )
}
